import { ConnectionState, ConnectionStatusInfo } from '../../common/types';
import logger from '@ayocore/platform/common/helpers/logger';

export class ConnectionStatus {
  private state: ConnectionState = ConnectionState.DISCONNECTED;
  private reconnectAttempts: number = 0;
  private lastConnected?: string;
  private lastError?: string;
  private statusHandlers: ((status: ConnectionStatusInfo) => void)[] = [];

  /**
   * Update connection state and notify handlers
   */
  setState(newState: ConnectionState): void {
    const oldState = this.state;
    this.state = newState;

    // Reset reconnect attempts on successful connection
    if (newState === ConnectionState.CONNECTED) {
      this.reconnectAttempts = 0;
      this.lastConnected = new Date().toISOString();
      this.lastError = undefined;
    }

    logger.info(`🔄 Connection state: ${oldState} → ${newState}`);
    this.notifyHandlers();
  }

  /**
   * Increment reconnection attempt counter
   */
  incrementReconnectAttempts(): number {
    this.reconnectAttempts++;
    logger.info(`🔄 Reconnect attempt: ${this.reconnectAttempts}`);
    this.notifyHandlers();
    return this.reconnectAttempts;
  }

  /**
   * Set last error message
   */
  setError(error: string): void {
    this.lastError = error;
    logger.error(`❌ Connection error: ${error}`);
    this.notifyHandlers();
  }

  /**
   * Get current status info
   */
  getStatus(): ConnectionStatusInfo {
    return {
      state: this.state,
      reconnectAttempts: this.reconnectAttempts,
      queuedMessages: 0, // Will be set by WebSocketConnection
      lastConnected: this.lastConnected,
      lastError: this.lastError,
    };
  }

  /**
   * Check if currently connected
   */
  isConnected(): boolean {
    return this.state === ConnectionState.CONNECTED;
  }

  /**
   * Check if currently connecting or reconnecting
   */
  isConnecting(): boolean {
    return this.state === ConnectionState.CONNECTING || this.state === ConnectionState.RECONNECTING;
  }

  /**
   * Register status change handler
   */
  onStatusChange(handler: (status: ConnectionStatusInfo) => void): void {
    this.statusHandlers.push(handler);
    // Immediately call with current status
    handler(this.getStatus());
  }

  /**
   * Remove status change handler
   */
  offStatusChange(handler: (status: ConnectionStatusInfo) => void): void {
    const index = this.statusHandlers.indexOf(handler);
    if (index !== -1) {
      this.statusHandlers.splice(index, 1);
    }
  }

  /**
   * Reset all status (for testing)
   */
  reset(): void {
    this.state = ConnectionState.DISCONNECTED;
    this.reconnectAttempts = 0;
    this.lastConnected = undefined;
    this.lastError = undefined;
    this.notifyHandlers();
  }

  /**
   * Notify all status handlers
   */
  private notifyHandlers(): void {
    const status = this.getStatus();
    this.statusHandlers.forEach(handler => {
      try {
        handler(status);
      } catch (error) {
        logger.error('❌ Error in status handler:', error);
      }
    });
  }
}
